import { api } from 'app/api';
import { IId, IPaginatedResponse } from 'types';
import { toFormData } from 'utils/toFormData';
import _isEmpty from 'lodash/isEmpty';
import {
  ICheckReferenceDuplicationMutation,
  BulkCreateReferencePayload,
  ICreateReferenceMutation,
  ICreateThreadMutation,
  ICreateThreadResponse,
  IEditReferenceMutation,
  IFileVersions,
  IGanttResult,
  IGetThreadsParams,
  IReferences,
  ISearchThreadsParams,
  ISearchThreadsResult,
  IStatusRequest,
  ITagsResponse,
  IThreadsResult,
  IThreadsDetails,
  IUpdateThreadMutation,
  TStatusResponse,
  ICheckReferenceDuplicationPayload,
} from './types';
import paths from '../../constants/path';

export const threadsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getThreadsList: build.query<IPaginatedResponse<IThreadsResult>, IGetThreadsParams>({
      query: ({ url, ...args }) => ({
        url,
        params: { ...args },
      }),
      providesTags: ['threadList'],
    }),

    getThreadsByRelatedThreads: build.query<
      IPaginatedResponse<IThreadsResult>,
      { relatedThreadIds: number[]; tags?: string[] }
    >({
      query: ({ relatedThreadIds, tags }) => ({
        url: paths.allThreadsList,
        params: {
          relatedThreads: relatedThreadIds,
          tags,
        },
      }),
      providesTags: ['threadListFilteredByRelatedThreads'],
    }),

    getGantt: build.query<IPaginatedResponse<IGanttResult>, IGetThreadsParams>({
      query: ({ url, ...args }) => ({
        url,
        params: { ...args },
      }),
    }),

    createThread: build.mutation<ICreateThreadResponse, ICreateThreadMutation>({
      query: (payload) => ({
        url: `/threads/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['threadList', 'statistic'],
    }),

    deleteThread: build.mutation<void, string>({
      query: (id) => ({
        url: `/threads/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['threadList', 'statistic'],
    }),

    updateThread: build.mutation<ICreateThreadMutation, IUpdateThreadMutation>({
      query: ({ id, ...payload }) => ({
        url: `/threads/${id}/`,
        method: 'PATCH',
        body: { ...payload },
      }),
      invalidatesTags: ['threadList', 'statistic', 'threadTags'],
    }),

    // useGetThreadQuery references this
    getThread: build.query<IThreadsResult, IId>({
      query: ({ id }) => `/threads/${id}/user-view/`,
      providesTags: (arg) => [
        { type: 'threadList', id: arg ? JSON.stringify(arg) : '0' },
        'threadList',
      ],
    }),

    getThreadDetails: build.query<IThreadsDetails, IId>({
      query: ({ id }) => `/thread-details/${id}/`,
      providesTags: [
        'events',
        'references',
        'contributorsList',
        'resources',
        'relatedThreads',
        'tdps',
      ],
    }),

    getSearchThreads: build.query<IPaginatedResponse<ISearchThreadsResult>, ISearchThreadsParams>({
      query: (args) => ({
        url: '/threads-search/',
        params: { ...args },
      }),
    }),

    getThreadTags: build.query({
      query: () => '/threads/tags-list/',
      transformResponse(response: ITagsResponse) {
        return response.tags;
      },
      providesTags: ['threadTags'],
    }),

    createReference: build.mutation<ICreateReferenceMutation, ICreateReferenceMutation>({
      query: (payload) => ({
        url: '/references/',
        method: 'POST',
        body: payload.file !== null ? toFormData(payload) : payload,
      }),
      invalidatesTags: ['references'],
    }),

    checkReferenceDuplication: build.mutation<
      ICheckReferenceDuplicationMutation,
      ICheckReferenceDuplicationPayload
    >({
      query: ({ file, threadId }) => ({
        url: `/references/check-filename-exists/?filename=${file.name}&thread_id=${threadId}`,
        method: 'GET',
      }),
    }),

    bulkCreateReferences: build.mutation<
      { succeeded: string[]; failed: string[] },
      BulkCreateReferencePayload[]
    >({
      // Create references as concurrent requests
      queryFn: async (payloads, _queryApi, _extraOptions, baseQuery) => {
        const requests = payloads.map((payload) =>
          baseQuery({
            url: '/references/',
            method: 'POST',
            body: toFormData(payload),
          })
        );
        // Promise.allSettled means we don't cancel when any fail
        const responses = await Promise.allSettled(requests);

        // Collect names of files that were and were not successfully uploaded
        const succeeded: string[] = [];
        const failed: string[] = [];
        responses.forEach((response, i) => {
          if (response.status === 'fulfilled') {
            // Failed API requests will still successfully resolve, check if error in response
            if ('error' in response.value) {
              failed.push(payloads[i].name);
            } else {
              succeeded.push(payloads[i].name);
            }
          } else {
            failed.push(payloads[i].name);
          }
        });
        return { data: { succeeded, failed } };
      },
      invalidatesTags: ['references'],
    }),

    getReference: build.query<IReferences, number | string>({
      query: (id) => `/references/${id}/`,
      providesTags: ['references'],
    }),

    referenceVersion: build.query<IFileVersions, number>({
      query: (id) => `/reference-versions/${id}/`,
      providesTags: ['reference-versions'],
    }),

    references: build.query<
      IPaginatedResponse<IReferences>,
      { id: string | undefined; limit: number }
    >({
      query: ({ id, limit = 10 }) => `threads/${id}/references/?limit=${limit}`,
      providesTags: ['references'],
    }),

    updateReference: build.mutation<IEditReferenceMutation, IEditReferenceMutation>({
      query: (payload) => ({
        url: `/references/${payload.id}/`,
        method: 'PATCH',
        body: payload.referenceType === 'LINK' ? payload : toFormData(payload),
      }),
      invalidatesTags: ['references'],
    }),

    deleteReference: build.mutation<void, string>({
      query: (id) => ({
        url: `/references/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['references'],
    }),

    getContributorsThreads: build.query<IPaginatedResponse<IThreadsResult>, IGetThreadsParams>({
      query: ({ url, ...args }) => ({
        url,
        params: { ...args },
      }),
    }),
    getViewabilityOfThreads: build.query<{ guestsCanViewPublicThreads: boolean }, void>({
      query: () => '/site-settings/',
      providesTags: ['threadViewability'],
    }),
    updateViewabilityOfThreads: build.mutation<void, { guestsCanViewPublicThreads: boolean }>({
      query: (payload) => ({
        url: '/site-settings/',
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['threadViewability'],
    }),
    getStatistic: build.query<TStatusResponse, IStatusRequest>({
      query: ({ dateFrom, type }) => ({
        url: `/threads/statistic/?type=${type}&updated_at__gte=${dateFrom}`,
      }),
      providesTags: ['statistic'],
      transformResponse: (response) => {
        return response as TStatusResponse;
      },
    }),
  }),
});

export const {
  useGetThreadsListQuery,
  useGetGanttQuery,
  useGetThreadQuery,
  useGetThreadDetailsQuery,
  useCreateThreadMutation,
  useUpdateThreadMutation,
  useDeleteThreadMutation,
  useGetThreadTagsQuery,
  useCreateReferenceMutation,
  useCheckReferenceDuplicationMutation,
  useUpdateReferenceMutation,
  useDeleteReferenceMutation,
  useGetContributorsThreadsQuery,
  useGetStatisticQuery,
  useGetSearchThreadsQuery,
  useGetReferenceQuery,
  useReferenceVersionQuery,
  useReferencesQuery,
  useGetViewabilityOfThreadsQuery,
  useUpdateViewabilityOfThreadsMutation,
  useGetThreadsByRelatedThreadsQuery,
  useBulkCreateReferencesMutation,
} = threadsListApi;
